import React, { useState, useEffect } from 'react';
import Plot from 'react-plotly.js';

const GraphComponent = ({ graphData, isLoading, contentId }) => {
  // State to track current theme
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute('data-theme') === 'dark'
  );

  const getCSSVariable = (variable) => {
    return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
  };

  // Listen for theme changes in graphs
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme');
          setIsDarkMode(newTheme === 'dark');
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  if (isLoading) {
    return (
      <div className="graph-container text-center">
          <div className="text-center" style={{marginTop:"22%"}}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
              </div>
          </div>
          <span className="ev-reg-p-dark">
            Generating Plot
          </span>
      </div>
    );
  }

  if (!graphData || !graphData.figure_dict) {
    return (
      <div className="container-error">
        <div className="text-center" >
          <p >Error loading graph</p>
        </div>
      </div>
    );
  }

  // Calculate theme colors when component renders or theme changes
  const themeColors = {
    paper_bgcolor: getCSSVariable('--background2'),
    plot_bgcolor: getCSSVariable('--background2'),
    fontColor: getCSSVariable('--color4'),
    gridColor: getCSSVariable('--color8'),
  };

  return (
    <div className="graph-container">
      <Plot
        data={graphData.figure_dict.data || []}
        layout={{
          ...graphData.figure_dict.layout,
          
          // Standard 2D chart theme colors
          paper_bgcolor: themeColors.paper_bgcolor,
          plot_bgcolor: themeColors.plot_bgcolor,
          font: {
            ...graphData.figure_dict.layout?.font,
            color: themeColors.fontColor
          },
          
          // 3D CHARTS: Scene background configuration
          scene: {
            ...graphData.figure_dict.layout?.scene,
            bgcolor: themeColors.plot_bgcolor,
            xaxis: {
              ...graphData.figure_dict.layout?.scene?.xaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.xaxis?.title,
                font: { color: themeColors.fontColor }
              },
              tickfont: { color: themeColors.fontColor }
            },
            yaxis: {
              ...graphData.figure_dict.layout?.scene?.yaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.yaxis?.title,
                font: { color: themeColors.fontColor }
              },
              tickfont: { color: themeColors.fontColor }
            },
            zaxis: {
              ...graphData.figure_dict.layout?.scene?.zaxis,
              backgroundcolor: themeColors.plot_bgcolor,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              title: {
                ...graphData.figure_dict.layout?.scene?.zaxis?.title,
                font: { color: themeColors.fontColor }
              },
              tickfont: { color: themeColors.fontColor }
            }
          },

          // RADAR/POLAR CHARTS: Polar background configuration
          polar: {
            ...graphData.figure_dict.layout?.polar,
            bgcolor: themeColors.plot_bgcolor,
            radialaxis: {
              ...graphData.figure_dict.layout?.polar?.radialaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { color: themeColors.fontColor },
              title: {
                ...graphData.figure_dict.layout?.polar?.radialaxis?.title,
                font: { color: themeColors.fontColor }
              }
            },
            angularaxis: {
              ...graphData.figure_dict.layout?.polar?.angularaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { color: themeColors.fontColor }
            }
          },

          // GEO CHARTS: Geographic background
          geo: {
            ...graphData.figure_dict.layout?.geo,
              bgcolor: themeColors.plot_bgcolor,
              lakecolor: themeColors.plot_bgcolor,
              landcolor: themeColors.plot_bgcolor,
              oceancolor: themeColors.plot_bgcolor,
              // Add these properties for country borders
              countrycolor: themeColors.fontColor,        // Country border color
              subunitcolor: themeColors.gridColor,        // Sub-unit border color (states/provinces)
              coastlinecolor: themeColors.fontColor,      // Coastline color
              framecolor: themeColors.fontColor,          // Frame color around the map
              projection: {
                ...graphData.figure_dict.layout?.geo?.projection
  
            }
          },

          // TERNARY CHARTS: Ternary background
          ternary: {
            ...graphData.figure_dict.layout?.ternary,
            bgcolor: themeColors.plot_bgcolor,
            aaxis: {
              ...graphData.figure_dict.layout?.ternary?.aaxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { color: themeColors.fontColor }
            },
            baxis: {
              ...graphData.figure_dict.layout?.ternary?.baxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { color: themeColors.fontColor }
            },
            caxis: {
              ...graphData.figure_dict.layout?.ternary?.caxis,
              gridcolor: themeColors.gridColor,
              linecolor: themeColors.fontColor,
              tickcolor: themeColors.fontColor,
              tickfont: { color: themeColors.fontColor }
            }
          },
          
          autosize: true,
          margin: { 
            l: 100,
            r: 100,
            t: 50,
            b: 120
          },
          
          // Standard X-axis
          xaxis: {
            ...graphData.figure_dict.layout?.xaxis,
            gridcolor: themeColors.gridColor,
            linecolor: themeColors.fontColor,
            tickcolor: themeColors.fontColor,
            tickangle: -45,
            automargin: true,
            tickmode: 'linear',
            categoryorder: 'category ascending',
            type: graphData.figure_dict.layout?.xaxis?.type || 'category',
            title: {
              ...graphData.figure_dict.layout?.xaxis?.title,
              font: {
                ...graphData.figure_dict.layout?.xaxis?.title?.font,
                color: themeColors.fontColor,
                size: 12
              }
            },
            tickfont: { 
              ...graphData.figure_dict.layout?.xaxis?.tickfont,
              color: themeColors.fontColor,
              size: 10
            }
          },
          
          // Standard Y-axis
          yaxis: {
            ...graphData.figure_dict.layout?.yaxis,
            gridcolor: themeColors.gridColor,
            linecolor: themeColors.fontColor,
            tickcolor: themeColors.fontColor,
            automargin: true,
            fixedrange: false,
            title: {
              ...graphData.figure_dict.layout?.yaxis?.title,
              font: {
                ...graphData.figure_dict.layout?.yaxis?.title?.font,
                color: themeColors.fontColor,
                size: 12
              }
            },
            tickfont: { 
              ...graphData.figure_dict.layout?.yaxis?.tickfont,
              color: themeColors.fontColor,
              size: 10
            }
          },
          
          // Legend
          legend: {
            ...graphData.figure_dict.layout?.legend,
            bgcolor: themeColors.paper_bgcolor,
            bordercolor: themeColors.gridColor,
            font: { color: themeColors.fontColor },
            orientation: 'h',
            x: 0,
            y: -0.15,
            xanchor: 'left',
            yanchor: 'top'
          },
          
          width: undefined,
          height: undefined
        }}
        config={{
          responsive: true,
          displayModeBar: true,
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
          doubleClick: 'reset',
          showTips: false
        }}
        style={{ 
          width: '100%', 
          height: '500px',
          maxWidth: '100%'
        }}
        useResizeHandler={true}
        key={isDarkMode ? 'dark' : 'light'}
      />
    </div>
  );
};

export default GraphComponent;

